import BonusFlag from "components/BonusFlag";
import CountdownV2 from "components/CountdownV2";
import { useGoogleAnalyticsContext } from "contexts/GoogleAnalyticsProvider";
import { useWebStoreContext } from "contexts/WebStoreProvider";
import { sortBundleRewards } from "helpers/rewardSort";
import { useAppDispatch } from "hooks";
import { WsDialogs } from "interfaces";
import { useEffect, useRef, useState } from "react";
import { featureTrackerAPI, offersApi } from "redux/apis";
import { useDialogModalRedux } from "redux/slices/dialog/hooks/useDialogModalRedux";
import bgLargeDefault from "./mock/bg_large_default.png";
import bgSmallDefault from "./mock/bg_small_default.png";
import { IFeaturedOfferCardProps } from "./types";

import { useMediaQuery } from "@mui/material";
import crownV2 from "assets/crownV2.png";
import infoIcon from "assets/header-banner/info-button.svg";
import classnames from "classnames";
import usePopUpsV2 from "components/PopUpManagerV2/hooks/usePopUpsV2";
import { onOpenPopUp } from "redux/slices";
import { getVariantImage } from "helpers/getVariantImage";
import useRewardsById from "../../../../hooks/useRewardsById";
import DebugCopyIdButton from "debug/components/DebugCopyIdButton";
import {
	Background,
	bannerStyle,
	Container,
	CrossOutValue,
	CustomButtom,
	DataContainer,
	InfoButton,
	InfoContainer,
	ItemImage,
	Items,
	LoyaltyContainer,
	LoyaltyPoints,
	OfferItem,
	RewardAmount,
	RewardAmountContainer,
	RewardItem,
	SubTitle,
	textStyle
} from "./styledV2";
import { FeatureType } from "interfaces/feature-tracker";
import RebateInfoPanel from "../../../../components/RebateOffer/RebateInfoPanel";
import { getTimeLeft } from "utils/time";

export const FeaturedOfferCardV2 = ({
	data: {
		id: offerId,
		rewardId,
		price,
		bannerText,
		loyaltyPointReward,
		timer,
		endTime,
		backgroundV2ImageUrl,
		//titleImageUrl,
		priority,
		campaignGroup,
		onlyNonPayers,
		purchaseLimit,
		loyaltyPointCrossout,
		upsellOfferConfig,
		rebateConfig
	},
	sectionName,
	from
}: IFeaturedOfferCardProps) => {
	const {
		authToken,
		currentOffer,
		userTracker,
		handleRefresh,
		nowTime,
		openLoginToContinueDialog,
		postUserMetadata,
		setActiveItemToBeClicked,
		setCurrentOffer,
		suppressOrderConfirmation,
		wsStoreBuy
	} = useWebStoreContext();
	const buyButtonRef = useRef(null);
	const dispatch = useAppDispatch();
	const { open } = usePopUpsV2();
	const rewardItems = useRewardsById(rewardId);
	const { openDialog } = useDialogModalRedux();
	const [isCurrentOffer, setIsCurrentOffer] = useState<boolean>(false);
	const { reportEvent, reportPlaygamiEvent } = useGoogleAnalyticsContext();
	// Temporarily hidden per product request; restore this state with the JSX below
	// to re-enable the Poi's Bounty messaging.
	// const { canContribute: isPoisActive } = useAppSelector(
	// 	selectPoisBountyV3State
	// );
	const uniqueKey = offerId;

	const isTimerEnabled = timer ? timer !== "false" : false;
	const isPortrait = useMediaQuery("(max-width:649px)");

	const resetCurrentOffer = () => {
		setIsCurrentOffer(false);
		setCurrentOffer(null);
	};

	//console.log("rewardId in FeaturedOfferCardV2:", rewardId);

	const onClickBuy = async () => {
		if (upsellOfferConfig) {
			open({
				variant: `bundle-up::${offerId}`,
				id: `bundle-up::${offerId}`,
				replaceIfExists: true
			});
			return;
		}
		setCurrentOffer(buyButtonRef.current);

		if (authToken?.value) {
			const invoice = await wsStoreBuy({ offerId });
			if (invoice && typeof invoice !== "number") {
				const timestampMs = new Date().getTime();
				openDialog(WsDialogs.XSOLLA_PURCHASE, {
					xsollaToken: invoice.xsollaToken,
					xsollaHost: invoice.xsollaHost,
					invoice,
					section: "shop",
					source: "featured_offer",
					pricePoint: price,
					suppressDips: rebateConfig
						? {
								purchaseSuccess: true,
								purchaseFailed: true,
								purchaseCancelled: true
						  }
						: undefined,
					onPurchaseStarted: (data) => {
						const minutesLeft = getTimeLeft(nowTime, endTime);
						reportEvent("purchase_started", {
							tuid: userTracker?.id as string,
							offer_id: offerId,
							reward_id: rewardId,
							priority: priority,
							price_in_cents: Math.round(price * 100),
							campaign_group: campaignGroup,
							section_name: sectionName,
							only_non_payers: onlyNonPayers,
							//event_time_client: eventTimeClient.toISO(),
							//minutes_left: minutesLeft,
							invoice_id: invoice?.id,
							paystation_id: data?.payStation
						});
						reportPlaygamiEvent("tp.web_ga.purchase_flow", {
							event_name: "purchase_started",
							event_type: "web_paystation_purchase",
							action: "purchase_started",
							section_name: sectionName,
							purchase_flow_id: data?.payStation,
							session_engaged: authToken?.value ? "1" : "0",
							price_in_cents: Math.round(invoice.price * 100),
							page_location: window.location.href,
							engagement_time_msec: (
								Number(data?.data?.milliseconds) - timestampMs
							).toString(),
							reward_id: invoice?.rewardId,
							offer_id: invoice?.offerId,
							ignore_referrer:
								process.env.REACT_APP_WITH_DEBUG === "true" ? "true" : "false",
							minutes_left: minutesLeft,
							page_title: location.pathname.replace("/", ""),
							page_referrer: invoice?.invoiceMetadata?.referer,
							priority: priority,
							invoice_id: invoice?.id
						});
					},
					onPurchaseSuccess: (data) => {
						if (rebateConfig) {
							dispatch(
								featureTrackerAPI.util.invalidateTags([
									{
										type: "featureTrackerTag",
										id: FeatureType.REBATE_OFFER
									}
								])
							);
							dispatch(onOpenPopUp(`rebate-main-popup::${"0"}`));
						}
						const minutesLeft = getTimeLeft(nowTime, endTime);
						reportEvent("purchase_success", {
							invoice_id: invoice?.id
						});
						reportPlaygamiEvent("tp.web_ga.purchase_flow", {
							event_name: "purchase_success",
							event_type: "web_paystation_purchase",
							action: "purchase_success",
							section_name: sectionName,
							purchase_flow_id: data?.version,
							session_engaged: authToken?.value ? "1" : "0",
							price_in_cents: Math.round(invoice.price * 100),
							page_location: window.location.href,
							engagement_time_msec: (
								Number(data?.milliseconds) - timestampMs
							).toString(),
							reward_id: invoice?.rewardId,
							offer_id: invoice?.offerId,
							ignore_referrer:
								process.env.REACT_APP_WITH_DEBUG === "true" ? "true" : "false",
							minutes_left: minutesLeft,
							page_title: location.pathname.replace("/", ""),
							page_referrer: invoice?.invoiceMetadata?.referer,
							priority: priority,
							invoice_id: invoice?.id,
							transaction_id: data?.purchase_invoice_id?.toString()
						});
					},
					cbClose: () => {
						invalidateFeaturedOffers();
						resetCurrentOffer();
						handleRefresh();
						reportEvent("dialog_x_closed", { source: "xsolla_dialog" });
					},
					suppressOrderConfirmation,
					postUserMetadata,
					showClose: true
				});
			} else {
				resetCurrentOffer();
			}

			//NOTE:bfloyd error cases already handled for undefined/null invoice
		} else {
			setActiveItemToBeClicked({
				type: "store_offer",
				offerId,
				onlyNonPayers,
				purchaseLimit
			});
			// claimMyReward();
			setActiveItemToBeClicked({
				type: "store_offer",
				offerId,
				onlyNonPayers,
				purchaseLimit
			});
			openLoginToContinueDialog();
		}
	};

	const invalidateFeaturedOffers = () => {
		dispatch(
			offersApi.util.invalidateTags([
				{
					type: "offersTag",
					id: "featured"
				}
			])
		);
	};

	const openInfoPopup = () => {
		const infoSource = from === "elite" ? "vip_shop" : "feature_deal";
		reportEvent("info_button_click", {
			source: infoSource,
			offer_id: offerId
		});
		reportPlaygamiEvent("tp.web_frontend", {
			event_name: "info_button_click",
			event_type: "feature_deal",
			action: "info_button_click",
			source: infoSource,
			offer_id: offerId
		});
		dispatch(onOpenPopUp("loyalty-points-info"));
	};

	const handleOnEnd = () => {
		invalidateFeaturedOffers();
	};

	useEffect(() => {
		if (currentOffer === buyButtonRef.current) {
			setIsCurrentOffer(true);
		} else {
			setIsCurrentOffer(false);
		}
	}, [currentOffer]);

	return (
		<Container id={offerId} data-testid="featuredCard" key={uniqueKey}>
			<Background
				imageUrl={
					backgroundV2ImageUrl
						? getVariantImage(
								backgroundV2ImageUrl,
								"{image_variant_1}",
								isPortrait ? "small" : "large"
						  )
						: isPortrait
						? bgSmallDefault
						: bgLargeDefault
				}
			/>
			{isTimerEnabled && endTime && (
				<CountdownV2
					text="Offer ends in:"
					startTime={nowTime}
					endTime={endTime}
					onEnd={handleOnEnd}
					style={{ padding: "0.2em 0.5em", top: "0.5em" }}
					//iconStyle={}
					//labelStyle={}
				/>
			)}
			<BonusFlag
				text={bannerText}
				position="right"
				bannerStyle={bannerStyle}
				textStyle={textStyle}
			/>
			{rebateConfig && <RebateInfoPanel rebateConfig={rebateConfig} />}
			<DataContainer>
				<DebugCopyIdButton
					id={offerId}
					label="OfferID"
					side="center"
					vertical="top"
					style={{ top: "-2.5em", fontSize: "0.8em", zIndex: 1000 }}
				/>
				<OfferItem>
					<Items>
						{sortBundleRewards(rewardItems).map(
							({ imageUrl, description }, key) => {
								return (
									<RewardItem
										className="imgWrap"
										key={key}
										width={100 / rewardItems.length + "%"}
									>
										<ItemImage src={imageUrl} alt="" />
										<RewardAmountContainer>
											<RewardAmount>{description}</RewardAmount>
										</RewardAmountContainer>
									</RewardItem>
								);
							}
						)}
					</Items>
				</OfferItem>
				<SubTitle>You also get:</SubTitle>
				<LoyaltyContainer>
					<ItemImage className="crown" src={crownV2} />
					<InfoContainer>
						<CrossOutValue>
							{loyaltyPointCrossout &&
								Number(loyaltyPointCrossout)?.toLocaleString("en-US")}
						</CrossOutValue>
						<LoyaltyPoints>
							{loyaltyPointReward.toLocaleString("en-US")} Loyalty Points
						</LoyaltyPoints>
						{/*
							{isPoisActive && (
								<PoisBounty>Contribute to Poi’s Bounty</PoisBounty>
							)}
						*/}
					</InfoContainer>
					<InfoButton
						src={infoIcon}
						onClick={() => openInfoPopup()}
					></InfoButton>
				</LoyaltyContainer>
				<CustomButtom
					id={`buy-button-${offerId}`}
					ref={buyButtonRef}
					variant="contained"
					color="success"
					size="large"
					onClick={onClickBuy}
					className={classnames({
						isCurrentOfferSet: !isCurrentOffer && !!currentOffer,
						isCurrentOffer: !!isCurrentOffer
					})}
				>
					{price.toString()} USD
				</CustomButtom>
			</DataContainer>
		</Container>
	);
};

FeaturedOfferCardV2.displayName = "FeaturedOfferCard";
